// src/padplanner/PadPlannerMap.jsx
//
// PadPlanner's map: a standalone MapLibre map, not MapView. The Operator
// Portal's reviewer map is the pattern (portal/PortalReviewerMap.jsx) and its
// header has the long reasons; the short ones that apply here:
//
//   * THE TILE SESSION CARRIES NO PROJECT. noProject: true, or a user with a
//     project selected in the shell sends it to /tile/session and gets 403
//     INVALID_PROJECT. ReviewTimeStripMap does not pass it, which is why this
//     map fetches its own token rather than reusing that component.
//   * ONE MlDraw PER MAP. Its source and layer ids are fixed literals.
//   * RIGHT-CLICK CLOSES A SHAPE. MlDraw's _dblclick is empty by design; the
//     consumer binds contextmenu.
//
// ── PERIMETERS ARE EDITED BY VERTEX, NEVER ROTATED OR SCALED ───────────────
//
// MlDraw's bbox handles move, scale and rotate the whole shape in lng/lat
// degrees, which skews it (design §17). So every committed shape goes straight
// into vertex-edit mode, which suppresses those handles: the only edits on
// offer are moving, adding and removing vertices.
//
// ── WHAT IS DRAWN ──────────────────────────────────────────────────────────
//
//   pins        one point per plan, colored by status, clickable: the
//               dashboard's card (given, fitted once when they first arrive),
//               or with autoPins one per outline too small to see at this zoom
//   perimeters  plans' outlines, colored by status, clickable; selectedId's
//               is drawn thicker and filled (feature-state on the plan id)
//   perimeter   this plan's saved outline
//   draft       an outline not yet saved (an uploaded file, a picked pad)
//   pads        /vtiles/well_pad, the org-scoped detected pads, pickable
//   raster      the entitled capture named by rasterKey, over the basemap;
//               its source starts at the dataset's minzoom (S-e5: nothing is
//               asked for where the tiler draws nothing), stops at its
//               maxzoom (THE DEPTH) and
//               carries rasterCredit (creditForCapture: Copernicus's line
//               for a Sentinel-2 composite), which the credits footer shows
//               while the capture is drawn (S-e slice 2). A
//               synthetic_test capture puts no banner on the map: the SYNTHETIC
//               badge on its time-strip chip labels it (S-e, amending DI4)
//
// ── THE BASEMAP ────────────────────────────────────────────────────────────
//
// Bottom to top: the Mapbox basemap, the AI PRIME capture, outlines, pins,
// drawing. The basemap is the user's pick (useBasemapStore padplannerBasemapId,
// satellite-streets-v12 by default, one choice for every PadPlanner map), set
// from the picker top right. A page that passes mapStyle (the dashboard's dark
// card) gets that style instead, and no picker. A new pick replaces the base
// source and layer at the bottom of the stack rather than calling setStyle,
// which would drop every source added at load; the source is replaced, not
// re-tiled, so the attribution, always shown expanded as Mapbox's terms
// require, changes with it.
//
// ── THE CAMERA ─────────────────────────────────────────────────────────────
//
// The map moves itself only on a first load or a user's action, never on a
// re-render. Pages hand it new objects all the time: every editor save and
// autosave answers with the plan (a new perimeter object, same shape), and
// so do the plan page's writes. Fitting on each new object snapped the view
// back to the opening zoom right after each edit (DB, 2026-10-29). So:
//
//   fitKey     the plan id. The view fits to `perimeter` once per key, when
//              the map first has both, and not again for that key: not on a
//              save, a token or local_epsg change, a 409 reload, a check
//              update or a layer toggle.
//   draft      a new unsaved outline (an uploaded file, a picked pad) is
//              fitted: only a user's action makes one.
//   focus      fitted once per focusKey (the list's selected plan, whose
//              outline may not be in the loaded viewport yet).
//   initialBounds, pins   fitted once per map. The pins' fit is redone when
//              the container is resized (MapLibre's own ResizeObserver
//              resizes the map) until the user moves the view: a dashboard
//              panel that settles square after the first fit still frames
//              every pin.
//
// Once the user pans or zooms (a movestart with an originalEvent: a drag,
// the wheel, the zoom buttons, the keyboard), a first-load fit that has not
// happened yet for that key is dropped: their view wins. A page that moves
// the camera because the user asked (the editor's Checks tab frames the
// features of a clicked row) passes USER_ACTION as the move's eventData, and
// that counts as the user's move too.
//
// ── CHROME ─────────────────────────────────────────────────────────────────
//
// Everything over the map is in MapHud.jsx's six slots, never placed by hand,
// except the credits footer along the bottom edge:
//   topLeft       MapLibre's zoom control, then the page's
//   topCenter     the page's (its toolbar), then the hint banner (the page's,
//                 or the draw mode's own)
//   topRight      the view toggle (when `view`), the basemap picker, the
//                 imagery error, then the page's
//   bottomLeft    the page's, then the readout card (the page's rows, plus
//                 the cursor when showCursor)
//   bottomCenter  the page's, then `bottom` (the time strip). `bottom` may be
//                 a function (S-e5): it gets {areas, center, zoom, zoomTo}
//                 for the strip's coverage at the map center, the map's own
//                 GET /imagery/entitled answer indexed (entitledCoverage.js;
//                 null until it answers), the center and zoom as the map
//                 moves, and zoomTo(z), a user's move to z keeping the center
//   bottomRight   the page's
//   footer        components/map/MapCreditsFooter.jsx (S-e slice 2, DB
//                 2026-11-02): about 24 px along the bottom edge, the map's
//                 canvas under it, the slots above it (MapHud's insetBottom,
//                 the footer's measured height). Mapbox's logo at the left,
//                 MapLibre's scale bar (scaleBar), the credits at the right
//                 (MapLibre's own attribution control is off: the footer
//                 draws the drawn sources' credits itself, so the Copernicus
//                 line can truncate while Mapbox's links stay whole). On
//                 Paper, no logo and no credits (no Mapbox tile is drawn):
//                 the grid readout and the scale bar.
//
// ── THE VIEW: IMAGERY, BASEMAP OR PAPER ────────────────────────────────────
//
// A page that passes `view` (mapView.js useMapView) gets the toggle. The mode
// only shows and hides layers: the basemap (hidden in Paper), the capture
// (shown in Imagery only), a background (Paper) and the grid (Paper;
// paperGridLayer.js, in gridEpsg's frame at gridStepFt). Nothing is added or
// removed and the camera is not touched (THE CAMERA), so a switch keeps the
// view exactly. Paper's tone (Dark or Light) colors the sheet, the grid and
// this plan's perimeter from paperTheme.js. Outlines-only and the layout's
// colors are the page's to apply to its FootprintEditor (setDrawingStyle),
// which this map does not own.
//
// A page passes its own as `hud` ({topLeft: node | node[], ...}). MapLibre's
// control corners are moved into the slots once the map is made (its
// bottom-right corner into the footer), so a control MapLibre adds later (the
// scale bar) lands in its place too.
import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { whenMapLoaded } from "../lib/map/whenMapLoaded";
import "maplibre-gl/dist/maplibre-gl.css";
import MlDraw from "../lib/ml-draw/ml-draw.js";
import { getOrFetchTileToken, bindTileAuthHandler } from "../api/tileTokens";
import { tileUrl } from "../api/tileUrl";
import { buildTransformRequest } from "../api/mapTransformRequest";
import { useAuthStore } from "../stores/useAuthStore";
import { useBasemapStore, PADPLANNER_BASEMAPS } from "../stores/useBasemapStore";
import BasemapSelector from "../components/LayerPanel/BasemapSelector";
import { BASE_ID, basemapSource, ppBasemapStyle } from "./ppMapStyle";
import MapCreditsFooter from "../components/map/MapCreditsFooter";
import { MapHint, MapReadout } from "./MapChrome";
import MapHud from "./MapHud";
import MapViewToggle, { PaperLegend } from "./MapViewToggle";
import { paperTheme } from "./paperTheme";
import { PaperGridLayer, PAPER_GRID_LAYER_ID, premultiplied } from "./paperGridLayer";
import { gridNorthDeg } from "./paperGrid";
import { createFrame, isUtmEpsg } from "../lib/footprint-editor/frame.js";
import { axisFrame } from "../lib/footprint-editor/padAxis.js";
import { statusColorExpression } from "./planStatus";
import { useEntitledFootprints } from "../hooks/useEntitledFootprints";
import { useFootprintOverlay } from "../hooks/useFootprintOverlay";
import { basemapTone, FP_LAYER } from "../lib/map/footprintStyle";
import EntitledMaskError from "../components/imagery/EntitledMaskError";
import { pinsBounds, editablePolygon, pinsFromPerimeters, boundsOf as geoBounds, USER_ACTION, USER_ACTION_FLAG } from "./planMapData";
import { entitledAreas } from "../layers/imagery/entitledCoverage";

/** No selected capture (a stable empty list). */
const NO_KEYS = Object.freeze([]);

const PAD_TILES = `${window.location.origin}/vtiles/well_pad/{z}/{x}/{y}.pbf`;
// This plan's perimeter, off Paper; on Paper, the tone's (paperTheme.js).
const PLAN_COLOR = "#38bdf8";
const SRC = Object.freeze({
  raster: "pp-raster", perimeters: "pp-perimeters", plan: "pp-plan", draft: "pp-draft", pads: "pp-pads", pins: "pp-pins",
});
const LAYER = Object.freeze({
  raster: "pp-raster", padsFill: "pp-pads-fill", padsLine: "pp-pads-line",
  perimetersFill: "pp-perimeters-fill", perimetersLine: "pp-perimeters-line",
  planFill: "pp-plan-fill", planLine: "pp-plan-line", draftLine: "pp-draft-line", pins: "pp-pins",
  paper: "pp-paper",
});
const EMPTY = { type: "FeatureCollection", features: [] };
const SELECTED = ["boolean", ["feature-state", "selected"], false];
const MIN_OUTLINE_PX = 14;
// The Permian, where the first plans are.
const DEFAULT_VIEW = { center: [-102.3, 31.9], zoom: 9 };
// THE DEPTH (DI4). The map goes to z23, for a 1 cm capture. The capture's
// raster source stops at its dataset's maxzoom (the tile session's), so past
// it MapLibre overzooms the deepest tiles it has instead of asking the tiler
// for z21-22 tiles the dataset was never cut for: a 10 cm capture (maxzoom 20)
// is drawn from z20 tiles at z21-23, which also means no requests past z20.
export const MAP_MAX_ZOOM = 23;

const asFC = (geometry) => (geometry ? { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry }] } : EMPTY);

function boundsOf(geometry) {
  const b = new maplibregl.LngLatBounds();
  const walk = (c) => (typeof c[0] === "number" ? b.extend(c) : c.forEach(walk));
  walk(geometry.coordinates);
  return b;
}

/**
 * @param {object} p
 * @param {object|null} [p.pins]         FeatureCollection of plan Points (planMapData.pinsFromPerimeters)
 * @param {object|null} [p.mapStyle]      a fixed base style, set once; none: the user's basemap pick
 * @param {object|null} [p.perimeters]   FeatureCollection of plan outlines (id, plan_number, status)
 * @param {(bbox: number[]) => void} [p.onViewport]   [w, s, e, n] after each move
 * @param {(planId: string) => void} [p.onPlanClick]
 * @param {string|null} [p.selectedId]   the outline to highlight
 * @param {object|null} [p.focus]        a geometry to fit the view to, once per focusKey
 * @param {string|null} [p.focusKey]     what `focus` belongs to (the selected plan's id)
 * @param {string|null} [p.fitKey]       the plan id: `perimeter` is fitted once per key (THE CAMERA)
 * @param {number[]|null} [p.initialBounds]  [w, s, e, n] to fit once, when it first arrives
 * @param {boolean} [p.autoPins]          a pin for each outline too small to see at the current zoom
 * @param {object|null} [p.perimeter]    this plan's saved geometry
 * @param {object|null} [p.draft]        an unsaved geometry to preview
 * @param {boolean} [p.editable]         arm MlDraw; the page's own toolbar drives it by `ref`
 * @param {import("react").Ref} [p.ref]  {startDraw(), startEdit(), clearDraw()}
 * @param {(mode: null|"polygon"|"edit") => void} [p.onDrawMode]  for a page's own toolbar
 * @param {(geometry: object|null) => void} [p.onDraw]  the drawn Polygon, or null when cleared
 * @param {boolean} [p.showPads]
 * @param {(observationId: string) => void} [p.onPadPick]  set to arm a one-click pad pick
 * @param {string|null} [p.rasterKey]
 * @param {string|null} [p.rasterCredit]  the capture's credit (planVocabulary.js creditForCapture), on its tile source
 * @param {string|null} [p.hint]          a banner at the top; the draw mode's own hint wins
 * @param {{label: string, value: string|null}[]} [p.readout]  rows for the bottom-left card
 * @param {boolean} [p.showCursor]        add the cursor's lat, lng to the readout
 * @param {boolean} [p.scaleBar]
 * @param {import("react").ReactNode|((strip: {areas: Map|null, center: number[], zoom: number, zoomTo: (z: number) => void}) => import("react").ReactNode)} [p.bottom]
 *   along the bottom edge (the time strip): the bottomCenter slot's last item; a function gets the
 *   strip's camera and coverage (CHROME)
 * @param {object} [p.hud]               the page's overlays by slot (MapHud.jsx HUD_SLOTS): {topLeft: node | node[], ...}
 * @param {boolean} [p.perimeterDashed]  draw this plan's outline dashed (the editor, as its mockup does); read once, at load
 * @param {object|null} [p.view]        mapView.js useMapView(): offer Imagery | Basemap | Paper (THE VIEW)
 * @param {number|null} [p.gridEpsg]     Paper's grid frame: the revision's local_epsg, or provisionalEpsg() before one
 * @param {number} [p.gridStepFt]        Paper's dot step: the editor's snap step, 5 ft elsewhere
 * @param {object|null} [p.gridAxis]     Paper's grid turned to the pad (padAxis.js {origin, deg}), or null for grid north
 * @param {object|null} [p.viewAlign]    {checked, onChange}: the view toggle's "Align grid to pad" (a read-only page's)
 * @param {(map: object|null) => void} [p.onMapReady]  the MapLibre map once loaded, and null before it is removed
 *   (the editor attaches a FootprintEditor to it; its ids are fe-*, never pp-*)
 * @param {{role: string, include?: string|null, withReference?: string|null}|null} [p.footprint]  the entitled mask (footprint restyle S-b):
 *   role is footprintStyle.js's (editor, newPlan, planPage, plansList), include a synthetic capture's
 *   dataset_id the page shows (includeForCapture), or the mask would darken it; withReference a
 *   Sentinel-2 capture's dataset_id the page shows (referenceForCapture, S-c4), whose entitled area
 *   the mask then opens. None: no mask (the
 *   dashboard). Paper view draws none either.
 */
export default function PadPlannerMap({
  pins = null, mapStyle = null, perimeters = null, onViewport, onPlanClick,
  selectedId = null, focus = null, focusKey = null, fitKey = null, initialBounds = null, autoPins = false, perimeter = null, draft = null,
  editable = false, ref = null, onDrawMode, onDraw, showPads = false, onPadPick = null, rasterKey = null, rasterCredit = null,
  hint = null, readout = [], showCursor = false, scaleBar = false, bottom = null, onMapReady = null, perimeterDashed = false,
  hud = {}, view = null, gridEpsg = null, gridStepFt = 5, gridAxis = null, viewAlign = null, footprint = null,
}) {
  const containerRef = useRef(null);
  // Where MapLibre's control corners go: into the HUD's slots (CHROME).
  const cornerHosts = useRef({ "top-left": null, "top-right": null, "bottom-left": null, "bottom-right": null });
  const dashedAtLoad = useRef(perimeterDashed);   // read once, when the layers are made
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const [ready, setReady] = useState(false);
  // The map as state once loaded, for the hooks that take one (the entitled mask).
  const [loadedMap, setLoadedMap] = useState(null);
  const [drawMode, setDrawMode] = useState(null);     // null | "polygon" | "edit"
  // Keyed by dataset: an error belongs to the capture that failed, so a new key clears it.
  const [rasterErrorKey, setRasterErrorKey] = useState(null);
  // What the tile session said about the capture drawn, keyed by dataset like the error.
  const [cursor, setCursor] = useState(null);
  const [zoom, setZoom] = useState(DEFAULT_VIEW.zoom);
  // The strip's camera (S-e5): the center and zoom on every move, as Explore's strip has them.
  const [camera, setCamera] = useState({ center: DEFAULT_VIEW.center, zoom: DEFAULT_VIEW.zoom });
  // THE VIEW. The mode as the load-time handlers read it, and the grid's legend.
  const mode = view?.mode ?? "imagery";
  const tone = view?.tone;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const [legend, setLegend] = useState({ tier: null, north: 0 });
  // The grid's frame: the editor's, turned to the pad when it is aligned (the same fromAxis the editor snaps with).
  const axisOrigin = gridAxis?.origin?.join(","), axisDeg = gridAxis?.deg;
  const gridFrame = useMemo(() => (isUtmEpsg(gridEpsg)
    ? axisFrame(createFrame(gridEpsg), gridAxis ? { origin: gridAxis.origin, deg: gridAxis.deg } : null) : null),
  [gridEpsg, axisOrigin, axisDeg]);   // eslint-disable-line react-hooks/exhaustive-deps
  const gridRef = useRef(null);
  if (!gridRef.current) {
    gridRef.current = new PaperGridLayer({
      onTier: (tier) => {
        const m = mapRef.current, f = gridRef.current?.frame;
        const c = m?.getCenter?.();
        setLegend({ tier, north: f && c ? gridNorthDeg(f, [c.lng, c.lat]) : 0 });
      },
    });
  }
  const basemapId = useBasemapStore((s) => s.padplannerBasemapId);
  const setBasemapId = useBasemapStore((s) => s.setPadplannerBasemap);
  // The style is chosen once, when the map is made; a later prop does not
  // rebuild it, and a new pick swaps only the base (below).
  const [base] = useState(() => ({
    style: mapStyle ?? ppBasemapStyle(useBasemapStore.getState().padplannerBasemapId),
    picked: !mapStyle,
  }));
  const shownBasemap = useRef(base.picked ? useBasemapStore.getState().padplannerBasemapId : null);
  // The capture's credit as the raster effect reads it: it follows the key, which the effect is keyed on.
  const creditRef = useRef(rasterCredit);
  creditRef.current = rasterCredit;
  // The credits footer's height: the HUD's slots end above it.
  const [footerHeight, setFooterHeight] = useState(0);
  // THE ENTITLED MASK (footprint restyle S-b): over the capture, under the pads,
  // the outlines and Paper's grid. Fail closed: the world is masked until
  // GET /imagery/entitled answers and after any error, which the HUD says with a Retry.
  const entitledFootprints = useEntitledFootprints({ enabled: Boolean(footprint), include: footprint?.include ?? null,
    withReference: footprint?.withReference ?? null });
  useFootprintOverlay({
    map: footprint ? loadedMap : null,
    anchor: LAYER.padsFill,
    role: mode === "paper" ? "paper" : footprint?.role ?? "dashboard",
    tone: base.picked ? basemapTone(basemapId) : "dark",
    entitled: entitledFootprints.entitled,
    // The selected capture (S-c1) is the one drawn: picked, else pinned, else the
    // newest opened by default; none on new plan until one is picked. The role
    // decides whether it shows (not on the plans list, the dashboard or Paper).
    selectedKeys: rasterKey ? [rasterKey] : NO_KEYS,
  });
  const footprintFailed = Boolean(footprint) && entitledFootprints.status === "error" && mode !== "paper";
  const areas = useMemo(() => entitledAreas(entitledFootprints.entitled, entitledFootprints.status),
    [entitledFootprints.entitled, entitledFootprints.status]);
  // Handlers are bound once, in the load handler; these keep them current.
  const cb = useRef({});
  // THE CAMERA: the key the view was last fitted for, and the key the user
  // last moved the view under.
  const keyNow = useRef(fitKey);
  keyNow.current = fitKey;
  const fittedKey = useRef(undefined);
  const userMovedKey = useRef(undefined);
  // Any user move, whatever the key: the pins' fit is never redone after one.
  const userMovedEver = useRef(false);
  const pinsFit = useRef(null);
  useLayoutEffect(() => {
    cb.current = { onViewport, onPlanClick, onDraw, onPadPick, showCursor };
  });

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: base.style,
      ...DEFAULT_VIEW,
      maxZoom: MAP_MAX_ZOOM,
      // The credits footer draws the credits (CHROME); MapLibre's control would draw them twice.
      attributionControl: false,
      // The pad tiles are entitlement-gated like every tile here; without the
      // bearer they 401 and nothing renders to pick (SitePage.jsx has it too).
      transformRequest: buildTransformRequest(() => useAuthStore.getState().token),
    });
    mapRef.current = map;
    bindTileAuthHandler(map);
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
    for (const [pos, host] of Object.entries(cornerHosts.current)) {
      const corner = containerRef.current.querySelector(`.maplibregl-ctrl-${pos}`);
      if (corner && host) host.appendChild(corner);
    }

    whenMapLoaded(map, () => {
      map.addSource(SRC.pads, { type: "vector", tiles: [PAD_TILES], minzoom: 8, maxzoom: 16 });
      // "assets": the MVT layer api.vector_tile names, for every asset type.
      map.addLayer({ id: LAYER.padsFill, type: "fill", source: SRC.pads, "source-layer": "assets",
        layout: { visibility: "none" }, paint: { "fill-color": "#22d3ee", "fill-opacity": 0.15 } });
      map.addLayer({ id: LAYER.padsLine, type: "line", source: SRC.pads, "source-layer": "assets",
        layout: { visibility: "none" }, paint: { "line-color": "#22d3ee", "line-width": 1 } });

      // promoteId: feature-state needs an id MapLibre accepts, and plan ids are UUIDs.
      map.addSource(SRC.perimeters, { type: "geojson", data: EMPTY, promoteId: "id" });
      map.addLayer({ id: LAYER.perimetersFill, type: "fill", source: SRC.perimeters,
        paint: { "fill-color": statusColorExpression(), "fill-opacity": ["case", SELECTED, 0.32, 0.1] } });
      map.addLayer({ id: LAYER.perimetersLine, type: "line", source: SRC.perimeters,
        paint: { "line-color": statusColorExpression(), "line-width": ["case", SELECTED, 3, 1.5] } });

      map.addSource(SRC.plan, { type: "geojson", data: EMPTY });
      map.addLayer({ id: LAYER.planFill, type: "fill", source: SRC.plan,
        paint: { "fill-color": PLAN_COLOR, "fill-opacity": 0.12 } });
      map.addLayer({ id: LAYER.planLine, type: "line", source: SRC.plan,
        paint: { "line-color": PLAN_COLOR, "line-width": 2, ...(dashedAtLoad.current ? { "line-dasharray": [3, 2] } : {}) } });

      map.addSource(SRC.pins, { type: "geojson", data: EMPTY, promoteId: "id" });
      map.addLayer({ id: LAYER.pins, type: "circle", source: SRC.pins,
        paint: { "circle-radius": ["case", SELECTED, 7, 5], "circle-color": statusColorExpression(),
          "circle-stroke-color": "rgba(7, 11, 16, 0.9)", "circle-stroke-width": 1.5 } });

      map.addSource(SRC.draft, { type: "geojson", data: EMPTY });
      map.addLayer({ id: LAYER.draftLine, type: "line", source: SRC.draft,
        paint: { "line-color": "#f472b6", "line-width": 2, "line-dasharray": [2, 1.5] } });

      map.on("click", LAYER.pins, (e) => {
        const id = e.features?.[0]?.properties?.id;
        if (id) cb.current.onPlanClick?.(id);
      });
      map.on("mouseenter", LAYER.pins, () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", LAYER.pins, () => { map.getCanvas().style.cursor = ""; });
      map.on("click", LAYER.perimetersFill, (e) => {
        const id = e.features?.[0]?.properties?.id;
        if (id) cb.current.onPlanClick?.(id);
      });
      map.on("click", (e) => {
        const pick = cb.current.onPadPick;
        if (!pick) return;
        const hits = map.queryRenderedFeatures(e.point, { layers: [LAYER.padsFill] });
        const id = hits[0]?.properties?.id ?? hits[0]?.id;
        if (typeof id === "string") pick(id);
      });
      map.on("zoomend", () => setZoom(map.getZoom()));
      const readCamera = () => {
        const c = map.getCenter?.(), z = map.getZoom?.();
        if (c && Number.isFinite(z)) setCamera({ center: [c.lng, c.lat], zoom: z });
      };
      map.on("move", readCamera);
      // Only a user's move carries originalEvent; a fitBounds does not,
      // unless a page passed USER_ACTION for a move the user asked for.
      map.on("movestart", (e) => {
        if (e?.originalEvent || e?.[USER_ACTION_FLAG]) { userMovedKey.current = keyNow.current; userMovedEver.current = true; }
      });
      // A resize's own movestart carries neither, so it never counts as the user's.
      map.on("resize", () => {
        const b = pinsFit.current;
        if (b && !userMovedEver.current) map.fitBounds(b, { padding: 40, maxZoom: 12, duration: 0 });
      });
      map.on("moveend", () => {
        const b = map.getBounds();
        cb.current.onViewport?.([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
      });
      map.on("mousemove", (e) => { if (cb.current.showCursor) setCursor(e.lngLat); });
      map.on("mouseout", () => setCursor(null));
      map.on("contextmenu", (e) => {
        const d = drawRef.current;
        if (!d || !d.drawing) return;
        if (d.mode === "polygon") d.completePolygon?.([e.lngLat.lng, e.lngLat.lat]);
      });
      readCamera();
      setReady(true);
      setLoadedMap(map);
      cb.current.onViewport?.((() => { const b = map.getBounds(); return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]; })());
    });

    return () => {
      if (mapRef.current && drawRef.current) mapRef.current.removeControl(drawRef.current);
      mapRef.current?.remove();
      mapRef.current = null;
      drawRef.current = null;
    };
    // base never changes (useState with no setter): this runs once.
  }, [base]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !base.picked || shownBasemap.current === basemapId) return;
    if (map.getLayer(BASE_ID)) map.removeLayer(BASE_ID);
    if (map.getSource(BASE_ID)) map.removeSource(BASE_ID);
    map.addSource(BASE_ID, basemapSource(basemapId));
    map.addLayer({ id: BASE_ID, type: "raster", source: BASE_ID, layout: { visibility: modeRef.current === "paper" ? "none" : "visible" } },
      map.getStyle().layers[0]?.id);
    shownBasemap.current = basemapId;
  }, [ready, base, basemapId]);

  // THE VIEW: Paper's sheet at the bottom and its grid over it, made once
  // when a page offers the view; then each mode shows and hides.
  const offersView = Boolean(view);
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !offersView || map.getLayer(LAYER.paper)) return;
    map.addLayer({ id: LAYER.paper, type: "background", layout: { visibility: "none" }, paint: { "background-color": paperTheme(tone).sheet } },
      map.getStyle().layers[0]?.id);
    map.addLayer(gridRef.current, LAYER.padsFill);
  }, [ready, offersView]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !offersView) return;
    const show = (id, on) => { if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none"); };
    const paper = mode === "paper";
    const t = paperTheme(tone);
    if (paper) map.setPaintProperty(LAYER.paper, "background-color", t.sheet);
    map.setPaintProperty(LAYER.planFill, "fill-color", paper ? t.perimeterFill : PLAN_COLOR);
    map.setPaintProperty(LAYER.planLine, "line-color", paper ? t.perimeter : PLAN_COLOR);
    show(LAYER.paper, paper);
    if (base.picked) show(BASE_ID, !paper);
    show(LAYER.raster, mode === "imagery");
    gridRef.current.set({
      frame: gridFrame, stepFt: gridStepFt, enabled: paper,
      colors: { dot: premultiplied(t.dot, t.dotAlpha), minor: premultiplied(t.line, t.minorAlpha), major: premultiplied(t.line, t.majorAlpha) },
    });
  }, [ready, offersView, mode, tone, base, gridFrame, gridStepFt]);

  // MlDraw only on an editable map: a read-only map registers no draw handlers.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !editable || drawRef.current) return;
    const draw = new MlDraw();
    // getAll(), not the argument: onUpdate() is also called with no argument on
    // every vertex drag (PortalReviewerMap.jsx has the six call sites).
    draw.onUpdate = () => {
      const feature = draw.getAll()?.features?.[0] ?? null;
      if (feature && !draw.vertexEditMode) { draw.enterVertexEdit(); setDrawMode("edit"); }
      cb.current.onDraw?.(feature ? feature.geometry : null);
    };
    map.addControl(draw);
    drawRef.current = draw;
  }, [ready, editable]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    map.getSource(SRC.perimeters)?.setData(perimeters ?? EMPTY);
  }, [ready, perimeters]);

  // The highlight follows the id, and is set again after each setData.
  const selectedRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !map.getSource(SRC.perimeters)) return;
    if (selectedRef.current && selectedRef.current !== selectedId) {
      for (const source of [SRC.perimeters, SRC.pins]) map.setFeatureState({ source, id: selectedRef.current }, { selected: false });
    }
    if (selectedId) {
      for (const source of [SRC.perimeters, SRC.pins]) map.setFeatureState({ source, id: selectedId }, { selected: true });
    }
    selectedRef.current = selectedId;
  }, [ready, selectedId, perimeters, pins, autoPins, zoom]);

  // Once per focusKey: a refetch of the same plan is a new object, not a new focus.
  const focusedKey = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!focusKey) { focusedKey.current = null; return; }
    if (!ready || !map || !focus || focusedKey.current === focusKey) return;
    focusedKey.current = focusKey;
    map.fitBounds(boundsOf(focus), { padding: 80, maxZoom: 16, duration: 300 });
  }, [ready, focus, focusKey]);

  // An outline smaller than this many pixels across is drawn as a pin as well:
  // at a basin's zoom a pad is a speck, and a speck can't be seen or clicked.
  // The size in pixels changes with the zoom, not with panning.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !autoPins) return;
    const tiny = (perimeters?.features ?? []).filter((f) => {
      const b = geoBounds(f.geometry);
      if (!b) return false;
      const a = map.project([b[0], b[1]]);
      const c = map.project([b[2], b[3]]);
      return Math.max(Math.abs(c.x - a.x), Math.abs(c.y - a.y)) < MIN_OUTLINE_PX;
    });
    map.getSource(SRC.pins)?.setData(pinsFromPerimeters({ type: "FeatureCollection", features: tiny }));
  }, [ready, autoPins, perimeters, zoom]);

  // Fit once, to the first bounds the page gives: after that the view is the user's.
  const boundsFitted = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !initialBounds || boundsFitted.current) return;
    boundsFitted.current = true;
    const [w, s, e, n] = initialBounds;
    map.fitBounds([[w, s], [e, n]], { padding: 60, maxZoom: 15, duration: 0 });
  }, [ready, initialBounds]);

  // Fit once, to the first pins that arrive: after that the view is the user's.
  const pinsFitted = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || autoPins) return;
    map.getSource(SRC.pins)?.setData(pins ?? EMPTY);
    const b = pinsBounds(pins);
    if (b && !pinsFitted.current) {
      pinsFitted.current = true;
      pinsFit.current = [[b[0], b[1]], [b[2], b[3]]];
      map.fitBounds(pinsFit.current, { padding: 40, maxZoom: 12, duration: 0 });
    }
  }, [ready, pins, autoPins]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    // While editing, MlDraw draws the shape; the saved outline would sit under it.
    map.getSource(SRC.plan)?.setData(drawMode === "edit" ? EMPTY : asFC(perimeter));
  }, [ready, perimeter, drawMode]);

  // A new draft is a user's action (a file, a pad): show it.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    map.getSource(SRC.draft)?.setData(asFC(draft));
    if (draft) map.fitBounds(boundsOf(draft), { padding: 60, maxZoom: 17, duration: 0 });
  }, [ready, draft]);

  // First load per plan: once per fitKey, unless the user moved first. A new
  // perimeter object for the same key (any save's answer) does nothing.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || fitKey == null || fittedKey.current === fitKey || !perimeter) return;
    fittedKey.current = fitKey;
    if (userMovedKey.current === fitKey) return;
    map.fitBounds(boundsOf(perimeter), { padding: 60, maxZoom: 17, duration: 0 });
  }, [ready, fitKey, perimeter]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const v = showPads || onPadPick ? "visible" : "none";
    map.setLayoutProperty(LAYER.padsFill, "visibility", v);
    map.setLayoutProperty(LAYER.padsLine, "visibility", v);
    map.getCanvas().style.cursor = onPadPick ? "crosshair" : "";
  }, [ready, showPads, onPadPick]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !scaleBar) return undefined;
    const scale = new maplibregl.ScaleControl({ unit: "imperial", maxWidth: 120 });
    map.addControl(scale, "bottom-right");
    return () => { if (mapRef.current) mapRef.current.removeControl(scale); };
  }, [ready, scaleBar]);

  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return undefined;
    let cancelled = false;
    if (map.getLayer(LAYER.raster)) map.removeLayer(LAYER.raster);
    if (map.getSource(SRC.raster)) map.removeSource(SRC.raster);
    if (!rasterKey) return undefined;
    (async () => {
      try {
        const { ck, minzoom, maxzoom } = await getOrFetchTileToken(rasterKey, { noProject: true });
        if (cancelled || map.getSource(SRC.raster)) return;
        map.addSource(SRC.raster, {
          type: "raster",
          // tileUrl, not a hand-built path. nginx's signed gateway matches only
          // /tiles/WebMercatorQuad/z/x/y.png; anything else under /tiles/ is
          // not a tile, so a path without ".png" draws nothing. PadPlanner
          // did exactly that until 13623736.
          tiles: [tileUrl(rasterKey, ck)],
          tileSize: 256,
          // The dataset's own depth (THE DEPTH); MapLibre's 22 when the session has none.
          ...(Number.isInteger(maxzoom) ? { maxzoom } : {}),
          // And its top (S-e5): below its minzoom the tiler answers empty tiles
          // (z8-11 on dev's 10 cm captures: 97% 67-byte PNGs), so none are asked for.
          ...(Number.isInteger(minzoom) ? { minzoom } : {}),
          // Copernicus's line on a Sentinel-2 composite: the footer shows it while this source is drawn.
          ...(creditRef.current ? { attribution: creditRef.current } : {}),
        });
        // Over the base, under the entitled mask and every outline.
        const above = [FP_LAYER.mask, PAPER_GRID_LAYER_ID, LAYER.padsFill].find((id) => map.getLayer(id));
        map.addLayer({ id: LAYER.raster, type: "raster", source: SRC.raster,
          layout: { visibility: modeRef.current === "imagery" ? "visible" : "none" } }, above);
      } catch (e) {
        // The error can carry the dataset key (invalid_dataset_key:…): the console
        // gets it, the page does not.
        console.warn("[padplanner] imagery failed to load", e);
        if (!cancelled) setRasterErrorKey(rasterKey);
      }
    })();
    return () => { cancelled = true; };
  }, [ready, rasterKey]);

  const editableShape = editablePolygon(perimeter);
  const shownHint = drawMode === "polygon" ? "Click to place corners, right-click to finish."
    : drawMode === "edit" ? "Drag a corner to move it, click an edge to add one, right-click a corner to remove it."
    : onPadPick ? "Click a detected pad to use its outline."
    : hint;
  const readoutRows = showCursor
    ? [...readout, { label: "Cursor", value: cursor ? `${cursor.lat.toFixed(5)}, ${cursor.lng.toFixed(5)}` : null }]
    : readout;

  function startDraw() {
    const d = drawRef.current;
    if (!d) return;
    d.exitVertexEdit?.();
    d.delete?.();
    d.startPolygon?.();
    setDrawMode("polygon");
  }
  function startEdit() {
    const d = drawRef.current;
    if (!d || !editableShape) return;
    d.stopDrawing?.();
    d.add?.({ type: "Feature", properties: {}, geometry: editableShape });
    d.enterVertexEdit?.();
    setDrawMode("edit");
  }
  function clearDraw() {
    const d = drawRef.current;
    d?.stopDrawing?.();
    d?.exitVertexEdit?.();
    d?.delete?.();
    setDrawMode(null);
  }
  useImperativeHandle(ref, () => ({ startDraw, startEdit, clearDraw }));
  // The strip's zoom-in chip (S-e5): to the capture's minzoom, the center kept, as the user's own move.
  const zoomTo = useCallback((z) => { mapRef.current?.easeTo({ zoom: z }, USER_ACTION); }, []);
  const shownBottom = typeof bottom === "function" ? bottom({ areas, center: camera.center, zoom: camera.zoom, zoomTo }) : bottom;
  // Once per map. A LAYOUT effect, so on unmount its cleanup (the null) runs
  // before the passive cleanup above that calls map.remove(): whatever was
  // attached takes its own layers off while the map still has its style.
  const readyCb = useRef(onMapReady);
  readyCb.current = onMapReady;
  useLayoutEffect(() => {
    if (!ready || !mapRef.current) return undefined;
    readyCb.current?.(mapRef.current);
    return () => readyCb.current?.(null);
  }, [ready]);
  useEffect(() => { onDrawMode?.(drawMode); }, [drawMode, onDrawMode]);

  const list = (x) => (Array.isArray(x) ? x : [x]);
  const corner = (pos) => <div key={`ctrl-${pos}`} ref={(el) => { cornerHosts.current[pos] = el; }} className="pp-hud-ctrl" data-hud-ctrl={pos} />;
  const slots = {
    topLeft: [corner("top-left"), ...list(hud.topLeft)],
    topCenter: [
      ...list(hud.topCenter), shownHint && <MapHint key="hint">{shownHint}</MapHint>],
    topRight: [
      corner("top-right"),
      view && <MapViewToggle key="view" view={view} align={viewAlign} />,
      base.picked && mode !== "paper" && <BasemapSelector key="basemap" overlay options={PADPLANNER_BASEMAPS} value={basemapId} onChange={setBasemapId} />,
      footprintFailed && <EntitledMaskError key="entitled-error" onRetry={entitledFootprints.retry} />,
      rasterKey && rasterErrorKey === rasterKey && mode === "imagery" && (
        <p key="raster-error" className="rounded bg-black/60 px-2 py-1 text-[11px] text-fg-secondary">
          This imagery couldn't be loaded. Showing the basemap.
        </p>
      ),
      ...list(hud.topRight),
    ],
    bottomLeft: [...list(hud.bottomLeft), corner("bottom-left"),
      readoutRows.some((r) => r.value) && <MapReadout key="readout" rows={readoutRows} />],
    bottomCenter: [...list(hud.bottomCenter), shownBottom && <div key="bottom" className="w-full">{shownBottom}</div>],
    bottomRight: list(hud.bottomRight),
  };
  // Paper draws no Mapbox tile, so it owes no credit: the footer keeps its place for the grid readout and the scale bar.
  const credited = mode !== "paper";

  return (
    <div className="relative h-full w-full" data-testid="padplanner-map" data-chip-frame data-view-mode={view ? mode : undefined}
      data-paper-tone={view && mode === "paper" ? (tone === "light" ? "light" : "dark") : undefined}>
      <div ref={containerRef} className="h-full w-full" />
      <MapHud slots={slots} insetBottom={footerHeight} />
      <MapCreditsFooter map={loadedMap} logo={credited} credits={credited} onHeight={setFooterHeight}>
        {mode === "paper" && <PaperLegend key="paper-legend" tier={legend.tier} northDeg={legend.north} axisDeg={gridFrame?.axis?.deg ?? null} />}
        {corner("bottom-right")}
      </MapCreditsFooter>
    </div>
  );
}
